import { Timer } from "./common/utils/timer";

type GetInfoResult = overwolf.games.launchers.events.GetInfoResult;

//------------------------------------------------------------------------------
export interface IOWLauncherEventsDelegate {
  onLauncherStarted(info: any);
  onLauncherStopped(info: any);
  onInfoUpdates(info: any);
  onNewEvents(e: any);
}

//------------------------------------------------------------------------------
export class OWLauncherEvents {
  private _delegate: IOWLauncherEventsDelegate;
  private settingRunes: Promise<any> | null;
  private cancelSettingRunes: Function | null;

  //----------------------------------------------------------------------------
  constructor(delegate: IOWLauncherEventsDelegate) {
    this._delegate = delegate;
    this.settingRunes = null;
    this.cancelSettingRunes = null;
  }

  //----------------------------------------------------------------------------
  private static get Constants() {
    return {
      LOL_LAUNCHER_ID: 10902,
      SET_FEATURE_RETRIES: 10,
      FEATURES: [
        'game_flow',
        'summoner_info',
        'champ_select',
        'lcu_info',
        'credentials',
        'lobby_info'
      ]
    }
  }

  //----------------------------------------------------------------------------
  static async getInfo(launcherId: number): Promise<GetInfoResult> {
    return new Promise((resolve) => {
      overwolf.games.launchers.events.getInfo(launcherId, resolve);
    })
  }

  //----------------------------------------------------------------------------
  static getRunningLOLLauncher(): Promise<any> {
    return new Promise(resolve => {
      overwolf.games.launchers.getRunningLaunchersInfo((res: any): void => {
        // console.log("getRunningLaunchersInfo():", res, OWLauncherEvents.Constants.LOL_LAUNCHER_ID);

        if ( !res || !res.launchers[0] )
          return resolve(false);

        // NOTE: we divide by 10 to get the launcher class id without it's sequence number
        if ( Math.floor(res.launchers[0].id/10) != OWLauncherEvents.Constants.LOL_LAUNCHER_ID )
          return resolve(false);

        return resolve(res.launchers[0]);
      });
    })
  }

  //----------------------------------------------------------------------------
  private async getInfo(): Promise<any> {
    return OWLauncherEvents.getInfo(OWLauncherEvents.Constants.LOL_LAUNCHER_ID);
  }

  //----------------------------------------------------------------------------
  private async setRequiredFeatures(): Promise<boolean> {
    let tries:number = 1,
        result;

    while ( tries <= OWLauncherEvents.Constants.SET_FEATURE_RETRIES ) {
      result = await new Promise(resolve => {
        overwolf.games.launchers.events.setRequiredFeatures(
          OWLauncherEvents.Constants.LOL_LAUNCHER_ID,
          OWLauncherEvents.Constants.FEATURES,
          resolve
        );
      })

      if ( result.status === 'success' ) {
        console.log('setRequiredFeatures(): success: '+ JSON.stringify(result, null, 2));
        return (result.supportedFeatures.length > 0);
      }

      await Timer.wait(3000);
      tries++;
    }

    console.warn('setRequiredFeatures(): failure after '+ tries +' tries'+ JSON.stringify(result, null, 2));
    return false;
  }

  //----------------------------------------------------------------------------
  private registerEvents(): void {
    // Remove, potential previous listeners
    this.unRegisterEvents();
    overwolf.games.launchers.events.onInfoUpdates.addListener(this.onInfoUpdates);
    overwolf.games.launchers.events.onNewEvents.addListener(this.onNewEvents);
  }

  //----------------------------------------------------------------------------
  private unRegisterEvents(): void {
    overwolf.games.launchers.events.onInfoUpdates.removeListener(this.onInfoUpdates);
    overwolf.games.launchers.events.onNewEvents.removeListener(this.onNewEvents);
  }

  //----------------------------------------------------------------------------
  private onLauncherStarted = async (info: any): Promise<void> => {
    if ( !info || info.classId !== OWLauncherEvents.Constants.LOL_LAUNCHER_ID )
      return;

    this.registerEvents();
    await this.setRequiredFeatures();
    // this.setRequiredFeatures();

    await Timer.wait(600);
    const { res, status } = await this.getInfo();

    this._delegate.onLauncherStarted(info);

    if ( res && status === 'success' )
      this.onInfoUpdates({ info: res });
  }

  //----------------------------------------------------------------------------
  private onLauncherStopped = (info: any): void => {
    this.unRegisterEvents();
    this._delegate.onLauncherStopped(info);
  }

  //----------------------------------------------------------------------------
  private onInfoUpdates = (info: any): void => {
    this._delegate.onInfoUpdates(info.info);
  }

  //----------------------------------------------------------------------------
  private onNewEvents = (e: any): void => {
    this._delegate.onNewEvents(e);
  }

  //----------------------------------------------------------------------------
  async setRunesSingle(arg1, arg2): Promise<any> {
    if ( this.settingRunes !== null && this.cancelSettingRunes !== null ) {
      this.cancelSettingRunes();
      await this.settingRunes;
    }

    return this.settingRunes = this.setRunesWithRetry(arg1, arg2);
  }

  //----------------------------------------------------------------------------
  private async setRunesWithRetry(arg1, arg2): Promise<any> {
    let success = false,
        canceled = false,
        tries = 20;

    this.cancelSettingRunes = () => { canceled = true };

    do {
      success = await this.setPages(arg1, arg2);
      tries--;

      console.log(`[OWLauncherEvents.setRunesWithRetry] setting try: ${(20 - tries)}, result: ${success}`);

      if ( !canceled && !success )
        await Timer.wait(3000);

    } while ( !canceled && !success && (tries > 0) );

    this.cancelSettingRunes = null;

    return success;
  }

  //----------------------------------------------------------------------------
  async setRunes(arg1, arg2): Promise<any> {
    console.log(`[OWLauncherEvents.setRunes] ${JSON.stringify(arg1)} ${JSON.stringify(arg2)}`);

    return this.setPages(arg1, arg2);
  }

  //----------------------------------------------------------------------------
  async setPages({token, port}, {title, primaryStyleId, selectedPerkIds, subStyleId}): Promise<any> {
    const
      baseUrl = `https://127.0.0.1:${port}`,
      pageName = 'porofessor';

    const headers = [
      { key: 'Accept', value: 'application/json' },
      { key: "Authorization", value: `Basic ${token}` },
    ];

    const pages:any = await new Promise((resolve): any => {
      overwolf.web.sendHttpRequest(
        `${baseUrl}/lol-perks/v1/pages`,
        overwolf.web.enums.HttpRequestMethods.GET,
        headers,
        null,
        result => {
          console.log(`[OWLauncherEvents.setPages] got pages: ${JSON.stringify(result)}`);

          if ( result.success) {
            try {
              resolve(JSON.parse(result.data));
            } catch (e) {
              resolve(result.data);
            }
          } else {
            resolve(null);
          }
        }
      )
    });

    let idToDelete = null;

    if ( pages && pages.length ) {
      let pageToDelete = pages.find(p => p.isDeletable && p.name.toLowerCase().includes(pageName));

      if ( !pageToDelete )
        pageToDelete = pages.find(p => p.isDeletable && p.current);

      if ( !pageToDelete )
        pageToDelete = pages.find(p => p.isDeletable);

      if ( pageToDelete && pageToDelete.id )
        idToDelete = pageToDelete.id;
    }

    if ( idToDelete !== null ) {
      const deleteResult:any = await new Promise((resolve): any => {
        overwolf.web.sendHttpRequest(
          `${baseUrl}/lol-perks/v1/pages/${idToDelete}`,
          overwolf.web.enums.HttpRequestMethods.DELETE,
          headers,
          null,
          r => resolve(r)
        )
      });

      console.log(`[OWLauncherEvents.setPages] DELETE code: ${deleteResult.statusCode} / response: ${JSON.stringify(deleteResult)}`);

      if ( !deleteResult.statusCode || deleteResult.statusCode < 200 || deleteResult.statusCode >= 300 )
        console.log(`[OWLauncherEvents.setPages] pages: ${JSON.stringify(pages)}`);

      await Timer.wait(2000);
    }

    const data = {
      id: 0,
      name: title,
      current: true,
      isActive: false,
      isDeletable: true,
      isEditable: true,
      isValid: true,
      order: 0,
      lastModified: Date.now(),
      autoModifiedSelections: [],
      primaryStyleId,
      selectedPerkIds,
      subStyleId,
    };

    const json = JSON.stringify(data);

    const postResult:any = await new Promise((resolve): any => {
      overwolf.web.sendHttpRequest(
        `${baseUrl}/lol-perks/v1/pages`,
        overwolf.web.enums.HttpRequestMethods.POST,
        headers,
        json,
        r => resolve(r)
      )
    });

    console.log(`[OWLauncherEvents.setPages] POST: ${postResult.statusCode} / response: ${JSON.stringify(postResult)}`);

    if ( !postResult.statusCode || postResult.statusCode < 200 || postResult.statusCode >= 300 )
      console.log(`[OWLauncherEvents.setPages] pages: ${JSON.stringify(pages)}`);

    return ( postResult.statusCode >= 200 && postResult.statusCode < 300 );
   }

  //----------------------------------------------------------------------------
  async start(): Promise<void> {
    console.log(`[ow-launch-events] START`);

    const running = await OWLauncherEvents.getRunningLOLLauncher();

    if ( running ) {
      console.log(`[ow-launch-events] lol launcher running`);
      this.onLauncherStarted(running);
    }

    overwolf.games.launchers.onLaunched.addListener(this.onLauncherStarted);
    overwolf.games.launchers.onTerminated.addListener(this.onLauncherStopped);
  }

  //----------------------------------------------------------------------------
  stop(): void {
    console.log(`[ow-launch-events] STOP`);

    this.unRegisterEvents();
  }
}
